import {
  localStorageCopySettings,
  WebflowStyleGuide,
} from 'base/copy_settings';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { Rows } from 'base/ui/layout';
import { menuItem } from 'base/ui/menu_model';
import { mount } from 'base/ui/overlay';
import { SelectButton } from 'base/ui/select';
import {
  TextRegularLight,
  TextSmallLight,
  TextTiny,
  TextTinyLight,
} from 'base/ui/typography';
import React, { useState } from 'react';
import styleGuideThumbnailV1_5Image from './style-guide-thumbnail-v1-5.png';
import styleGuideThumbnailV2Image from './style-guide-thumbnail-v2.png';
import styleGuideThumbnailV3Image from './style-guide-thumbnail-v3.png';
import styles from './style_guide_selection_dialog.css';

const STYLE_GUIDE_OPTIONS = [
  {
    label: 'Relume Style Guide V3 - CF V2.1 (recommended)',
    value: WebflowStyleGuide.RelumeV3_CF2_1,
  },
  {
    label: 'Relume Style Guide V2 - CF V2.1',
    value: WebflowStyleGuide.RelumeV2_CF2_1,
  },
  {
    label: 'Relume Style Guide V1.5 - CF V2',
    value: WebflowStyleGuide.RelumeV1_5_CF2,
  },
  {
    label: 'Finsweet Style Guide - CF V2.1',
    value: WebflowStyleGuide.Finsweet_CF2_1,
  },
  {
    label: 'Finsweet Style Guide - CF V2',
    value: WebflowStyleGuide.Finsweet_CF2,
  },
];

const StyleGuideSelectionDialog = ({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (value: WebflowStyleGuide) => void;
}) => {
  const { value: localStorageValue } = localStorageCopySettings;

  const [selectedStyleGuide, setSelectedStyleGuide] =
    useState<WebflowStyleGuide>(localStorageValue.style);

  return (
    <Dialog
      title="Select a Style Guide before pasting into Webflow"
      buttons={
        <Button
          label="Save"
          variant="primary"
          size="large"
          onClick={() => onSave(selectedStyleGuide)}
        />
      }
      onClose={onClose}
    >
      <Rows spacing={16}>
        <div className={styles.cards}>
          <div className={styles.card}>
            <div
              className={styles.cardThumbnail}
              style={{
                backgroundImage: `url(${styleGuideThumbnailV3Image})`,
              }}
            />
            <div className={styles.cardTitle}>
              <TextTinyLight>Version 3</TextTinyLight>
              <div className={styles.lozenge}>
                <TextTiny>New</TextTiny>
              </div>
            </div>
          </div>
          <div className={styles.card}>
            <div
              className={styles.cardThumbnail}
              style={{
                backgroundImage: `url(${styleGuideThumbnailV2Image})`,
              }}
            />
            <div className={styles.cardTitle}>
              <TextTinyLight>Version 2</TextTinyLight>
            </div>
          </div>
          <div className={styles.card}>
            <div
              className={styles.cardThumbnail}
              style={{
                backgroundImage: `url(${styleGuideThumbnailV1_5Image})`,
              }}
            />
            <div className={styles.cardTitle}>
              <TextTinyLight>Version 1.5</TextTinyLight>
            </div>
          </div>
        </div>
        <TextRegularLight>
          Style Guide V3 is now live! Select the Style Guide version your
          Webflow project is using to ensure copy and pasting pages/sections
          into Webflow syncs without errors.
        </TextRegularLight>
        <Rows spacing={8}>
          <TextSmallLight color="grey">Style Guide</TextSmallLight>
          <SelectButton
            menu={STYLE_GUIDE_OPTIONS.map(({ label, value }) =>
              menuItem({
                label,
                onClick: () => setSelectedStyleGuide(value),
                checked: selectedStyleGuide === value,
              }),
            )}
            size="large"
            fullWidth
            weight="light"
          />
        </Rows>
      </Rows>
    </Dialog>
  );
};

export const createStyleGuideSelectionDialog = () => {
  const showDialog = async () => {
    return new Promise<WebflowStyleGuide | undefined>((resolve) => {
      const close = mount(
        <StyleGuideSelectionDialog
          onClose={() => {
            close();
            resolve(undefined);
          }}
          onSave={(value) => {
            close();
            resolve(value);
          }}
        />,
      );
    });
  };

  return {
    showDialog,
  };
};
